import { Box, Typography } from '@mui/material'
import SchoolOutlinedIcon from '@mui/icons-material/SchoolOutlined'
import ChevronRightIcon from '@mui/icons-material/ChevronRight'
import { type carenetworkService } from 'core/types'

export interface EnrollmentCardProps {
  service: carenetworkService
  onClick?: (service: carenetworkService) => void
}

interface StatusStyle {
  label: string
  dotColor: string
  textColor: string
  backgroundColor: string
}

const STATUS_STYLES: Record<string, StatusStyle> = {
  PendingRelease: { label: 'Pending Release', dotColor: '#8F8F8F', textColor: '#514F53', backgroundColor: '#ECEDEF' },
  Referred: { label: 'Referred', dotColor: '#2F80ED', textColor: '#2F80ED', backgroundColor: '#E8F1FD' },
  Processing: { label: 'Processing', dotColor: '#7136BC', textColor: '#7136BC', backgroundColor: '#E0F2F1' },
  WaitListed: { label: 'Wait Listed', dotColor: '#B8860B', textColor: '#7A4D00', backgroundColor: '#FFF7E6' },
  Enrolled: { label: 'Enrolled', dotColor: '#1C6B3C', textColor: '#1C6B3C', backgroundColor: '#EAF6EF' },
  Started: { label: 'Started', dotColor: '#1C6B3C', textColor: '#1C6B3C', backgroundColor: '#EAF6EF' },
  Exited: { label: 'Exited', dotColor: '#8F8F8F', textColor: '#514F53', backgroundColor: '#ECEDEF' },
  Declined: { label: 'Declined', dotColor: '#9B1C1C', textColor: '#9B1C1C', backgroundColor: '#FDECEC' }
}

function getStatusStyle (status: string): StatusStyle {
  return STATUS_STYLES[status] ?? { label: status, dotColor: '#8F8F8F', textColor: '#514F53', backgroundColor: '#ECEDEF' }
}

export function EnrollmentCard (props: EnrollmentCardProps): JSX.Element {
  const { service } = props
  const statusStyle = getStatusStyle(service.status)

  return (
    <Box
      data-testid={`enrollment-card-${String(service.serviceID)}`}
      onClick={props.onClick !== undefined ? () => { props.onClick?.(service) } : undefined}
      sx={{
        backgroundColor: '#ECEDEF',
        borderRadius: '10px',
        display: 'flex',
        alignItems: 'center',
        gap: '10px',
        pl: '4px',
        pr: '20px',
        py: '16px',
        cursor: props.onClick !== undefined ? 'pointer' : 'default'
      }}
    >
      <Box sx={{ alignSelf: 'stretch', width: '4px', borderRadius: '2px', backgroundColor: service.requiresAction ? '#BC3656' : 'transparent' }} />
      <SchoolOutlinedIcon sx={{ color: '#222024', fontSize: '40px' }} />
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: '4px', flex: '1 0 0', minWidth: 0 }}>
        <Typography sx={{ color: '#222024', fontSize: '14px', fontWeight: 600 }} data-testid='enrollmentNameTextDisplay'>
          {service.serviceName}
        </Typography>
        <Typography sx={{ color: '#514F53', fontSize: '12px' }} data-testid='enrollmentProviderTextDisplay'>
          {service.serviceProviderName}
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px', backgroundColor: statusStyle.backgroundColor, borderRadius: '16px', px: '10px', py: '4px' }}>
            <Box sx={{ width: '8px', height: '8px', borderRadius: '50%', backgroundColor: statusStyle.dotColor }} />
            <Typography sx={{ color: statusStyle.textColor, fontSize: '11px', fontWeight: 600 }}>{statusStyle.label}</Typography>
          </Box>
          {service.requiresAction && (
            <Typography sx={{ color: '#BC3656', fontSize: '11px', fontWeight: 600 }} data-testid='enrollmentActionRequiredLabel'>
              Action Required
            </Typography>
          )}
        </Box>
      </Box>
      {props.onClick !== undefined && <ChevronRightIcon sx={{ color: '#514F53' }} />}
    </Box>
  )
}
